import { CardUpload } from '@cannect/new-components/molecules'
import { message, Result } from 'antd'
import { NoToken } from 'assets/svg/result'
import LoadingContainer from 'components/LoadingContainer'
import Text from 'components/Text'
import { CheckCircle } from 'lucide-react'
import Button from 'new-components/Button'
import Heading from 'new-components/Heading'
import Modal from 'new-components/Modal'
import React, { useEffect, useState } from 'react'
import { RiCheckFill, RiErrorWarningFill } from 'react-icons/ri'
import styled from 'styled-components'
import api from '../../../../../../services/api'
import { CartContainer, CartHeader, CartTitle } from '../styles'

// Types
type DocumentTypes = 'RESIDENCIAL' | 'RECEITA' | 'IDENTIDADE' | 'LAUDO_MEDICO'

interface FormattedDocumentsTypes {
  id: number
  type: DocumentTypes
  url: string
  name: string
  status: string
  extension: string
}

type DocumentByTypeData = {
  [key in DocumentTypes]: FormattedDocumentsTypes[]
}

interface PublicDocumentResponseTypes extends DocumentByTypeData {
  order_id?: number
}

// Styled Components
const Wrapper = styled.main`
  width: 100%;
  padding: 0;
`

const HeadingStyled = styled.h1`
  padding: 16px 0;
  margin-bottom: 10px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;

  span {
    color: #02b0bb;
  }
`

const ContentUpload = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
`

const ModalContent = styled.div<{ color?: string }>`
  width: 350px;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 16px 0;

  .icon-box {
    svg {
      width: 70px;
      height: 70px;
      color: ${({ color }) => (color === 'success' ? '#02b0bb' : '#dc2626')};
    }
  }

  h2 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #111827;
    margin: 1rem 0 0.5rem;
  }

  span {
    font-family: 'Nunito', sans-serif;
    font-weight: 400;
    font-size: 14px;
    text-align: center;
    color: #6b7280;
  }

  .action-box {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 1.5rem;
  }

  button {
    background: ${({ color }) => (color === 'success' ? '#02b0bb' : '#dc2626')};
    color: white;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.2s;

    &:hover {
      opacity: 0.9;
    }
  }

  @media (max-width: 768px) {
    width: 280px;
  }
`

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;

  .icon {
    width: 80px;
    height: 80px;
    background: #d1fae5;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1.5rem;

    svg {
      color: #059669;
      width: 40px;
      height: 40px;
    }
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    color: #111827;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 1rem;
    color: #6b7280;
    margin-bottom: 2rem;
    max-width: 400px;
  }

  button {
    background: #02b0bb;
    color: white;
    border: none;
    border-radius: 8px;
    padding: 0.75rem 2rem;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;

    &:hover {
      background: #018a94;
    }
  }
`

// Text Input Data
const TEXTINPUTDATA = {
  RECEITA: {
    title: 'Receita médica',
    description:
      'Certifique-se de que a receita tenha sido emitida nos últimos 6 meses e esteja assinada por um profissional da saúde com CRM ou CRO válido.'
  },
  IDENTIDADE: {
    title: 'Documento de identidade (RG, CPF ou CNH)',
    description: 'Certifique-se que o CPF está regular na receita federal'
  },
  RESIDENCIAL: {
    title: 'Comprovante de residência',
    description:
      'Comprovante emitido nos últimos 5 meses, em nome do paciente que consta na receita e com CEP do endereço de entrega.'
  },
  LAUDO_MEDICO: {
    title: 'Laudo médico',
    description: 'Laudo médico emitido pelo médico'
  }
}

// Status Modal Component
function StatusModal({
  isOpen,
  onClose,
  title,
  text,
  icon,
  textButton,
  variant = 'success',
  onClickButton
}: {
  isOpen: boolean
  onClose: () => void
  title?: string
  text?: React.ReactNode
  icon: React.ReactNode
  textButton: string
  variant?: 'success' | 'error'
  onClickButton: () => void
}) {
  if (!isOpen) return null

  return (
    <Modal isOpen={isOpen} setOpenModal={onClose} title="Status do Documento">
      <ModalContent color={variant}>
        <div className="icon-box">{icon}</div>
        <Heading elementType="h2" defaultColor="dark" fontSize="18px">
          {title}
        </Heading>
        <Text>{text}</Text>
        <div className="action-box">
          <button onClick={onClickButton}>{textButton}</button>
        </div>
      </ModalContent>
    </Modal>
  )
}

// Document Services
const documentTypeArray: DocumentTypes[] = ['RESIDENCIAL', 'RECEITA', 'IDENTIDADE', 'LAUDO_MEDICO']

const handleSaveDocument = async ({
  type,
  order_id,
  file
}: {
  type: DocumentTypes
  order_id: number
  file: File
}): Promise<FormattedDocumentsTypes | { message: string }> => {
  const url =
    type === 'RECEITA'
      ? `/order/send_recipe/${order_id}`
      : `/order/document/${order_id}?type=${type}&emission=18%2F11%2F2022`

  try {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await api.post(url, formData)

    const responseData = type === 'RECEITA' ? data?.recipeSalved : data?.document
    const linkUrl = type === 'RECEITA' ? responseData.link_pdf : responseData?.url

    if (responseData) {
      return {
        ...responseData,
        status: 'new_file',
        type,
        url: linkUrl || responseData?.url
      }
    }
    throw new Error()
  } catch (e) {
    console.error(e)
    return { message: 'Não foi possível enviar o arquivo, tente novamente.' }
  }
}

const getOrderDocuments = async (orderId: number): Promise<DocumentByTypeData | { message: string }> => {
  try {
    const { data } = await api.get(`/order/${orderId}/documents`)

    if (data.success) {
      const documents = data?.order?.documents?.flat() || data?.documents?.flat() || []
      const recipes = data?.order?.recipies || data?.order?.recipes || data?.recipies || data?.recipes || []

      const formattedRecipes = recipes.map((recipe: any) => ({
        id: recipe.id,
        type: 'RECEITA',
        url: recipe.url || recipe.link_pdf,
        name: recipe.filename,
        status: recipe.status,
        extension: recipe.extension
      }))

      const formattedDocument = documents.map((document: any) => ({
        id: document.id,
        type: document.type,
        url: document.url,
        name: document.originalName,
        status: document.status,
        extension: document.extension
      }))

      const returnData = [...formattedRecipes, ...formattedDocument]
      const transformedObject = documentTypeArray.reduce(
        (acc, key) => {
          acc[key] = returnData.filter((item: any) => item.type === key && item.status !== 'denied')
          return acc
        },
        {} as Record<DocumentTypes, any>
      )

      return transformedObject as DocumentByTypeData
    }

    throw new Error()
  } catch (e) {
    return { message: 'Não foi possível buscar lista de documentos.' }
  }
}

// Props
interface DocumentsProps {
  orderId: number | null
  onComplete?: () => void
  onBack?: () => void
}

// Main Component
export default function Documents({ orderId, onComplete, onBack }: DocumentsProps) {
  const [documentsList, setDocumentsList] = useState<PublicDocumentResponseTypes | null>(null)
  const [loadingDocuments, setLoadingDocuments] = useState(false)
  const [allDocumentsUploaded, setAllDocumentsUploaded] = useState(false)
  const [statusModal, setStatusModal] = useState({
    error: false,
    isOpen: false
  })

  const checkAllDocumentsUploaded = (docs: DocumentByTypeData) => {
    const hasReceita = docs.RECEITA?.length > 0
    const hasIdentidade = docs.IDENTIDADE?.length > 0
    const hasResidencial = docs.RESIDENCIAL?.length > 0

    // Receita, identidade e comprovante de residência são obrigatórios (laudo médico é opcional)
    if (hasReceita && hasIdentidade && hasResidencial) {
      setAllDocumentsUploaded(true)
    }
  }

  const fetchDocuments = async () => {
    if (!orderId) return

    setLoadingDocuments(true)
    const data = await getOrderDocuments(orderId)

    if ('message' in data) {
      message.error(data.message)
    } else {
      setDocumentsList(data as PublicDocumentResponseTypes)
      checkAllDocumentsUploaded(data as DocumentByTypeData)
    }

    setLoadingDocuments(false)
  }

  const handleUpload = async ({ file, type }: { file: File; type: DocumentTypes }) => {
    if (!orderId) return { success: false }

    try {
      const data = await handleSaveDocument({ order_id: orderId, file, type })

      if ('id' in data) {
        setDocumentsList((prevState) => {
          if (!prevState) return prevState
          const newState = {
            ...prevState,
            [type]: [...(prevState[type] || []), data]
          }
          checkAllDocumentsUploaded(newState)
          return newState
        })

        setStatusModal({
          error: false,
          isOpen: true
        })

        return { success: true, data }
      }
      throw new Error('Erro ao processar arquivo')
    } catch (error: any) {
      setStatusModal({
        error: true,
        isOpen: true
      })

      return { success: false, error }
    }
  }

  useEffect(() => {
    if (orderId) {
      fetchDocuments()
    }
  }, [orderId])

  if (!orderId) {
    return (
      <CartContainer>
        <Result
          icon={<img src={NoToken} alt="Sem token" />}
          title="Nenhum pedido foi identificado."
          extra={
            <Button onClick={onBack}>
              Voltar para <strong>meus pedidos</strong>
            </Button>
          }
        />
      </CartContainer>
    )
  }

  if (allDocumentsUploaded) {
    return (
      <CartContainer>
        <SuccessContainer>
          <div className="icon">
            <CheckCircle />
          </div>
          <h2>Documentos enviados com sucesso!</h2>
          <p>
            Todos os documentos foram recebidos. Nossa equipe irá analisar e você receberá uma confirmação em breve.
          </p>
          <button onClick={onComplete}>Concluir</button>
        </SuccessContainer>
      </CartContainer>
    )
  }

  return (
    <CartContainer>
      <CartHeader>
        <CartTitle>Envio de Documentos</CartTitle>
      </CartHeader>

      <Wrapper>
        <HeadingStyled>
          Envio de <span>documentos</span>
        </HeadingStyled>

        <StatusModal
          onClickButton={() =>
            setStatusModal({
              error: false,
              isOpen: false
            })
          }
          variant={statusModal.error ? 'error' : 'success'}
          icon={statusModal.error ? <RiErrorWarningFill /> : <RiCheckFill />}
          textButton={statusModal.error ? 'Tentar Enviar Novamente' : 'Continuar'}
          isOpen={statusModal.isOpen}
          onClose={() =>
            setStatusModal({
              error: false,
              isOpen: false
            })
          }
          title={statusModal.error ? 'Erro ao anexar o seu documento' : 'Documento anexado com sucesso'}
          text={
            statusModal.error ? (
              <div style={{ textAlign: 'left' }}>
                <p style={{ marginBottom: '12px' }}>
                  Não foi possível processar o documento enviado. Isso pode ter ocorrido por um dos seguintes motivos:
                </p>
                <ul style={{ paddingLeft: '20px', marginBottom: '12px' }}>
                  <li>
                    O tipo de documento enviado não corresponde ao solicitado (ex: documento de identidade enviado no
                    campo de comprovante de residência)
                  </li>
                  <li>O arquivo está protegido por senha</li>
                  <li>O arquivo está corrompido ou ilegível</li>
                  <li>Nossa IA não conseguiu interpretar o conteúdo do documento</li>
                  <li>Ocorreu um erro interno no processamento</li>
                </ul>
                <p>Por favor, verifique se o documento está correto e legível, e tente enviá-lo novamente.</p>
              </div>
            ) : null
          }
        />

        {loadingDocuments ? (
          <LoadingContainer loading />
        ) : (
          <ContentUpload>
            {(!documentsList?.RECEITA || documentsList.RECEITA?.length === 0) && (
              <CardUpload
                buttonClasses="h-full w-full md:w-[86px] md:min-w-[86px] rounded-none md:p-0 py-2 bg-[#02b0bb] text-white"
                showDescriptionFilesAccept
                currentFilesList={documentsList?.RECEITA || []}
                title={TEXTINPUTDATA.RECEITA.title}
                description={TEXTINPUTDATA.RECEITA.description}
                isRequired
                onUploadFile={(file) => handleUpload({ file, type: 'RECEITA' })}
              />
            )}

            {(!documentsList?.IDENTIDADE || documentsList.IDENTIDADE?.length === 0) && (
              <CardUpload
                buttonClasses="h-full w-full md:w-[86px] md:min-w-[86px] rounded-none md:p-0 py-2 bg-[#02b0bb] text-white"
                showDescriptionFilesAccept
                currentFilesList={documentsList?.IDENTIDADE || []}
                title={TEXTINPUTDATA.IDENTIDADE.title}
                description={TEXTINPUTDATA.IDENTIDADE.description}
                onUploadFile={(file) => handleUpload({ file, type: 'IDENTIDADE' })}
              />
            )}

            {(!documentsList?.RESIDENCIAL || documentsList.RESIDENCIAL?.length === 0) && (
              <CardUpload
                buttonClasses="h-full w-full md:w-[86px] md:min-w-[86px] rounded-none md:p-0 py-2 bg-[#02b0bb] text-white"
                showDescriptionFilesAccept
                currentFilesList={documentsList?.RESIDENCIAL || []}
                title={TEXTINPUTDATA.RESIDENCIAL.title}
                description={TEXTINPUTDATA.RESIDENCIAL.description}
                onUploadFile={(file) => handleUpload({ file, type: 'RESIDENCIAL' })}
              />
            )}

            {(!documentsList?.LAUDO_MEDICO || documentsList.LAUDO_MEDICO?.length === 0) && (
              <CardUpload
                buttonClasses="h-full w-full md:w-[86px] md:min-w-[86px] rounded-none md:p-0 py-2 bg-[#02b0bb] text-white"
                showDescriptionFilesAccept
                currentFilesList={documentsList?.LAUDO_MEDICO || []}
                title={TEXTINPUTDATA.LAUDO_MEDICO.title}
                description={TEXTINPUTDATA.LAUDO_MEDICO.description}
                onUploadFile={(file) => handleUpload({ file, type: 'LAUDO_MEDICO' })}
              />
            )}
          </ContentUpload>
        )}
      </Wrapper>
    </CartContainer>
  )
}
